import {
  Mesh,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  Vector2,
  type WebGLRenderer,
} from 'three'

export type ScreenTransitionPhase = 'idle' | 'covering' | 'covered' | 'revealing'
export type ScreenTransitionEffect = 'phone-focus' | 'dots'

const vertexShader = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`

const fragmentShader = /* glsl */ `
  uniform float uProgress;
  uniform float uTime;
  uniform float uEffect;
  uniform vec2 uResolution;
  varying vec2 vUv;

  float random(vec2 value) {
    return fract(sin(dot(value, vec2(12.9898, 78.233))) * 43758.5453);
  }

  void main() {
    vec2 uv = vUv;
    float aspect = uResolution.x / max(uResolution.y, 1.0);

    if (uEffect > 0.5) {
      // Aspect-correct cells keep the dots circular on wide and tall screens.
      vec2 dotSpace = vec2(uv.x * aspect, uv.y) * 14.0;
      vec2 cellId = floor(dotSpace);
      vec2 local = fract(dotSpace) - 0.5;
      float delay = random(cellId) * 0.18;
      float dotProgress = clamp((uProgress - delay) / max(1.0 - delay, 0.001), 0.0, 1.0);
      float radius = dotProgress * 0.76;
      float antialias = 21.0 / max(uResolution.y, 1.0);
      float dotAlpha = 1.0 - smoothstep(radius - antialias, radius + antialias, length(local));
      dotAlpha *= smoothstep(0.0, 0.025, dotProgress);
      gl_FragColor = vec4(vec3(0.0), dotAlpha);
      return;
    }

    float wobble = sin(uv.y * 22.0 + uTime * 4.2) * 0.016;
    wobble += sin((uv.x * aspect + uv.y) * 11.0 - uTime * 2.6) * 0.012;

    float sweep = uv.x + uv.y * 0.27 + wobble;
    float threshold = mix(-0.25, 1.36, uProgress);
    float coverage = 1.0 - smoothstep(threshold, threshold + 0.115, sweep);
    float edge = 1.0 - smoothstep(0.0, 0.115, abs(sweep - threshold));
    float stripe = step(0.54, fract((uv.x - uv.y * 0.18 + uTime * 0.22) * 13.0));
    float grain = random(floor(uv * uResolution.xy * 0.22) + floor(uTime * 18.0));

    vec3 navy = vec3(0.035, 0.055, 0.13);
    vec3 coral = vec3(0.98, 0.32, 0.38);
    vec3 gold = vec3(1.0, 0.79, 0.27);
    vec3 color = mix(navy, coral, edge * 0.62);
    color = mix(color, gold, edge * stripe * 0.5);
    color += (grain - 0.5) * 0.045;

    float alpha = clamp(coverage + edge * 0.58, 0.0, 1.0);
    gl_FragColor = vec4(color, alpha);
  }
`

const easeInOutCubic = (value: number): number =>
  value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2

export class ScreenTransition {
  private readonly scene = new Scene()
  private readonly camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1)
  private readonly geometry = new PlaneGeometry(2, 2)
  private readonly progressUniform = { value: 0 }
  private readonly timeUniform = { value: 0 }
  private readonly effectUniform = { value: 0 }
  private readonly resolutionUniform = { value: new Vector2(1, 1) }
  private readonly material = new ShaderMaterial({
    uniforms: {
      uProgress: this.progressUniform,
      uTime: this.timeUniform,
      uEffect: this.effectUniform,
      uResolution: this.resolutionUniform,
    },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  })
  private readonly mesh = new Mesh(this.geometry, this.material)
  private phase: ScreenTransitionPhase = 'idle'
  private phaseStartedAt = 0
  private phaseDurationMs = 1
  private revealDurationMs = 480
  private coverResolve: (() => void) | null = null
  private revealResolve: (() => void) | null = null
  private revealPromise: Promise<void> | null = null

  public constructor() {
    this.mesh.frustumCulled = false
    this.scene.add(this.mesh)
  }

  public playCover(
    durationMs: number,
    revealDurationMs = 480,
    effect: ScreenTransitionEffect = 'phone-focus',
  ): Promise<void> {
    this.finishPendingCover()
    this.finishPendingReveal()
    this.setEffect(effect)
    this.phase = 'covering'
    this.phaseStartedAt = performance.now()
    this.phaseDurationMs = Math.max(180, durationMs)
    this.revealDurationMs = Math.max(120, revealDurationMs)
    this.progressUniform.value = 0

    return new Promise((resolve) => {
      this.coverResolve = resolve
    })
  }

  /** Hold a completely covered frame while the scene graph is replaced. */
  public coverInstant(
    effect: ScreenTransitionEffect = 'dots',
    revealDurationMs = 900,
  ): void {
    this.finishPendingCover()
    this.finishPendingReveal()
    this.setEffect(effect)
    this.phase = 'covered'
    this.phaseStartedAt = performance.now()
    this.revealDurationMs = Math.max(120, revealDurationMs)
    this.progressUniform.value = 1
  }

  public reveal(durationMs = this.revealDurationMs): Promise<void> {
    if (this.phase === 'idle') return Promise.resolve()
    if (this.phase === 'revealing') return this.revealPromise ?? Promise.resolve()
    if (this.phase === 'covering') {
      this.progressUniform.value = 1
      this.finishPendingCover()
    }
    this.phase = 'revealing'
    this.phaseStartedAt = performance.now()
    this.phaseDurationMs = Math.max(120, durationMs)
    this.revealPromise = new Promise((resolve) => {
      this.revealResolve = resolve
    })
    return this.revealPromise
  }

  public revealDots(durationMs = 900): Promise<void> {
    this.setEffect('dots')
    return this.reveal(durationMs)
  }

  public update(timeMs: number): void {
    this.timeUniform.value = timeMs / 1000
    if (this.phase !== 'covering' && this.phase !== 'revealing') return

    const elapsed = Math.max(0, timeMs - this.phaseStartedAt)
    const normalized = Math.min(1, elapsed / this.phaseDurationMs)
    const eased = easeInOutCubic(normalized)
    this.progressUniform.value = this.phase === 'covering' ? eased : 1 - eased

    if (normalized < 1) return
    if (this.phase === 'covering') {
      this.phase = 'covered'
      this.progressUniform.value = 1
      this.finishPendingCover()
    } else {
      this.phase = 'idle'
      this.progressUniform.value = 0
      this.finishPendingReveal()
    }
  }

  public render(renderer: WebGLRenderer): void {
    if (this.phase === 'idle') return
    renderer.getDrawingBufferSize(this.resolutionUniform.value)
    const autoClear = renderer.autoClear
    renderer.autoClear = false
    renderer.clearDepth()
    renderer.render(this.scene, this.camera)
    renderer.autoClear = autoClear
  }

  private finishPendingCover(): void {
    const resolve = this.coverResolve
    this.coverResolve = null
    resolve?.()
  }

  private finishPendingReveal(): void {
    const resolve = this.revealResolve
    this.revealResolve = null
    this.revealPromise = null
    resolve?.()
  }

  private setEffect(effect: ScreenTransitionEffect): void {
    this.effectUniform.value = effect === 'dots' ? 1 : 0
  }

  public get state(): ScreenTransitionPhase {
    return this.phase
  }

  public dispose(): void {
    this.finishPendingCover()
    this.finishPendingReveal()
    this.phase = 'idle'
    this.scene.clear()
    this.geometry.dispose()
    this.material.dispose()
  }
}
